import "../../../assets/scss/instance-groups-style.scss";

import { useEffect, useState } from "react";
import {
  Button,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Table,
  Tag,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CaretUpOutlined,
  CaretDownOutlined,
  LoadingOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { MeshInstanceGroupManager } from "../meshInstanceGroupManager";

import {
  addInstanceToInstanceGroup,
  createInstanceGroup,
  deleteInstanceGroup,
  getInstanceGroupList,
  removeInstanceFromInstanceGroup,
} from "../../server/InstanceGroup";
import { getInstanceStatus } from "../../utility/common/common";
import { getInstances } from "../../server/Instances";
import { useAppSelector } from "../../store/hooks";

import "./style.scss";

interface Instance {
  instance_id: string;
  template_name: string;
  cpu: string;
  status: 0 | 1 | 2;
  product_name: string;
  node_name: string;
  internal_ip: string;
  healthy: number;
}

export const InstanceGroup = () => {
  const { t } = useTranslation();
  const userState = useAppSelector((state: any) => state.user);
  const [instanceGroupList, setInstanceGroupList] = useState<any>(undefined);
  const [addNewInstanceGroupGroupLoading, setAddNewInstanceGroupGroupLoading] =
    useState<boolean>(false); // add modal group loading
  const [newInstanceGroupGroupName, setNewInstanceGroupGroupName] =
    useState<string>("");
  const [
    newInstanceGroupGroupDescription,
    setNewInstanceGroupGroupDescription,
  ] = useState<string>("");
  const [newInstanceGroupGroupModal, setNewInstanceGroupGroupModal] =
    useState<boolean>(false);
  const [availableInstances, setAvailableInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [modalVisibleId, setModalVisibleId] = useState<number | null>(null);

  useEffect(() => {
    if (userState.user_id) {
      fetchInstanceList();
    }
  }, [userState]);

  const fetchInstanceList = async () => {
    setLoading(true);
    try {
      const response = await getInstances();
      const data = await response.data;
      if (data.instances !== null) {
        const formattedInstances = data.instances.map((instance: any) => ({
          instance_id: instance.instance_id,
          template_name: instance.template_name,
          cpu: instance.template_name,
          status: instance.healthy || 0,
          product_name: instance.product_name,
          healthy: instance.healthy || 0,
        }));
        setAvailableInstances(formattedInstances);
      } else {
        setAvailableInstances([]);
      }
    } catch {
      setAvailableInstances([]);
    } finally {
      setLoading(false);
    }
  };

  const getUsedInstanceIds = (): string[] => {
    return instanceGroupList.flatMap((instanceGroup: any) =>
      instanceGroup.instances.map((i: Instance) => i.instance_id)
    );
  };

  const columns: any[] = [
    {
      title: t("Instance Group ID"),
      dataIndex: "instance_group_id",
      key: "instance_group_id",
    },
    { title: t("Instance Group Name"), dataIndex: "name", key: "name" },
    {
      title: t("Instance Group Description"),
      dataIndex: "description",
      key: "description",
    },

    {
      title: "Action",
      dataIndex: "",
      key: "x",
      render: (_: any, record: any) => (
        <>
          <Popconfirm
            title={t("Delete Instance Group")}
            description={t("Are you sure to delete this instance group?")}
            okText={t("Yes")}
            disabled={loading || isDeleting}
            cancelText={t("No")}
            onConfirm={() => {
              const clusterToDelete = instanceGroupList.find(
                (item: any) =>
                  item.instance_group_id === record.instance_group_id
              );
              const newData = instanceGroupList.filter(
                (item: any) =>
                  item.instance_group_id !== record.instance_group_id
              );
              const reclaimedInstances = clusterToDelete?.instances ?? [];
              setIsDeleting(true);

              deleteInstanceGroup(record.instance_group_id)
                .then((res) => {
                  if (res.data.code === 0) {
                    // Deduplicate by ID
                    setAvailableInstances((prev) => {
                      const combined = [...prev, ...reclaimedInstances];
                      const uniqueMap = new Map();
                      combined.forEach((instance) => {
                        uniqueMap.set(instance.id, instance);
                      });
                      return Array.from(uniqueMap.values());
                    });
                    setIsDeleting(false);
                    setInstanceGroupList(newData);
                  }
                })
                .catch((err) => {
                  setIsDeleting(false);
                  message.warning(
                    t(
                      "Instance group is not empty, remove all instances first."
                    )
                  );
                });
            }}
          >
            <button className="primary-btn" disabled={isDeleting || loading}>
              {isDeleting ? <LoadingOutlined /> : t("Delete")}
            </button>
          </Popconfirm>
        </>
      ),
    },
    Table.EXPAND_COLUMN,
  ];

  const getInstanceColumns = (
    selectedInstanceGroupId: number
  ): ColumnsType<Instance> => [
    { title: "ID", dataIndex: "instance_id", key: "instance_id" },
    {
      title: t("Template Name"),
      dataIndex: "template_name",
      key: "template_name",
    },
    {
      title: t("GPU Model"),
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: t("Node Name"),
      dataIndex: "node_name",
      key: "node_name",
    },
    {
      title: t("Status"),
      dataIndex: "healthy",
      key: "healthy",
      render: (healthy: number) => (
        <div className="healthy-status-wrapper">
          <div
            className={`healthy-status ${
              healthy === 1 ? "healthy" : "not-healthy"
            }`}
          ></div>
          <span style={{ marginLeft: ".5em" }}>
            {t(getInstanceStatus(healthy))}
          </span>
        </div>
      ),
    },
    {
      title: "Internal IP",
      key: "internal",
      render: (_: any, record: Instance) => (
        <CopyOutlined
          onClick={() => navigator.clipboard.writeText(record.internal_ip)}
          style={{
            cursor: "pointer",
            fontSize: 16,
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: Instance) => (
        <Button
          type="link"
          danger
          onClick={() => {
            setLoading(true);
            removeInstanceFromInstanceGroup(
              selectedInstanceGroupId,
              record.instance_id
            )
              .then((res) => {
                let data = res.data.code;
                if (data === 0) {
                  const updated = instanceGroupList.map(
                    (instanceGroup: any) => {
                      if (
                        instanceGroup.instance_group_id ===
                        selectedInstanceGroupId
                      ) {
                        return {
                          ...instanceGroup,
                          instances: instanceGroup.instances.filter(
                            (inst: Instance) =>
                              inst.instance_id !== record.instance_id
                          ),
                        };
                      }
                      return instanceGroup;
                    }
                  );
                  setInstanceGroupList(updated);
                  setLoading(false);
                }
              })
              .catch((err) => {
                setLoading(false);
                console.log(err);
              });
          }}
        >
          {t("Disconnect")}
        </Button>
      ),
    },
  ];

  const addNewInstanceGroup = (data: any) => {
    let { name, description, instance_group_id } = data;
    setInstanceGroupList([
      ...instanceGroupList,
      {
        key: instance_group_id,
        name,
        description,
        instance_group_id: instance_group_id,
        instances: [],
      },
    ]);
    setNewInstanceGroupGroupName("");
    setNewInstanceGroupGroupDescription("");
    setNewInstanceGroupGroupModal(false);
  };

  const handleAddInstances = (
    selectedInstanceGroupId: number,
    instances: Instance[]
  ) => {
    setLoading(true);
    const updatedList = instanceGroupList.map((item: any) =>
      item.instance_group_id === selectedInstanceGroupId
        ? { ...item, instances: instances }
        : item
    );
    let instanceIds = instances.map((item) => item.instance_id);

    addInstanceToInstanceGroup(selectedInstanceGroupId.toString(), instanceIds)
      .then((res) => {
        let data = res.data;
        if (data.code === 0) {
          setInstanceGroupList(updatedList);
          setAddNewInstanceGroupGroupLoading(false);
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const populateInstanceGroupList = async () => {
    try {
      const response = await getInstanceGroupList();
      const data = await response.data.instance_groups;
      const patchedData = data.map((group: any, groupIndex: number) => ({
        ...group,
        key: group.instance_group_id ?? `group-${groupIndex}`, // 主表格用的唯一 key
        instances: (group.instances ?? []).map(
          (instance: any, idx: number) => ({
            ...instance,
          })
        ),
      }));

      setInstanceGroupList(patchedData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    populateInstanceGroupList();
  }, []);

  return (
    <>
      <div className="wrapper">
        <div className="clusters-title-wrapper">
          <div className="title">
            <h4>{t("Instance Group List")}</h4>
            <button
              className="primary-btn"
              onClick={() => setNewInstanceGroupGroupModal(true)}
            >
              {t("Add Instance Group")}
            </button>
          </div>
        </div>
        <div className="cluster-table-wrapper">
          {instanceGroupList === undefined ? (
            <Skeleton active />
          ) : (
            <Table
              className="table-striped-rows"
              tableLayout="fixed"
              columns={columns}
              dataSource={instanceGroupList}
              expandable={{
                expandedRowRender: (record) => {
                  const currentCapacity = record.instances.length;
                  return (
                    <div className="expand-content">
                      {loading || isDeleting ? (
                        <Skeleton active />
                      ) : (
                        <>
                          {currentCapacity > 0 ? (
                            <Table
                              scroll={{ x: true }}
                              columns={getInstanceColumns(
                                record.instance_group_id
                              )}
                              dataSource={record.instances}
                              pagination={false}
                              rowKey="instance_id"
                            />
                          ) : (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description={t("No Data Yet")}
                            />
                          )}
                        </>
                      )}
                      <div style={{ marginTop: 16 }}>
                        <Button
                          type="primary"
                          disabled={loading || isDeleting}
                          onClick={() =>
                            setModalVisibleId(record.instance_group_id)
                          }
                        >
                          {t("Add Instances")}
                        </Button>
                      </div>

                      <MeshInstanceGroupManager
                        visible={modalVisibleId === record.instance_group_id}
                        onClose={() => setModalVisibleId(null)}
                        onConfirm={(instances) =>
                          handleAddInstances(
                            record.instance_group_id,
                            instances
                          )
                        }
                        clusterName={record.name}
                        existingInstances={record.instances ?? []}
                        availableInstances={availableInstances.filter(
                          (inst) =>
                            !getUsedInstanceIds().includes(inst.instance_id)
                        )}
                      />
                    </div>
                  );
                },
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? (
                    <CaretUpOutlined
                      onClick={(e: any) => onExpand(record, e)}
                    />
                  ) : (
                    <CaretDownOutlined
                      onClick={(e: any) => onExpand(record, e)}
                    />
                  ),
              }}
            />
          )}
        </div>
      </div>

      <Modal
        title={t("Add New Instance Group")}
        centered
        open={newInstanceGroupGroupModal}
        footer={null}
        onCancel={() => setNewInstanceGroupGroupModal(false)}
      >
        {addNewInstanceGroupGroupLoading ? (
          <LoadingOutlined />
        ) : (
          <div className="instance-groups-add-form-wrapper">
            <div className="add-form">
              <div className="item">
                <span>{t("Instance Group Name")}</span>
                <input
                  type="text"
                  className="instance-group-input"
                  value={newInstanceGroupGroupName}
                  onChange={(e) => setNewInstanceGroupGroupName(e.target.value)}
                />
              </div>
              <div className="item">
                <span>{t("Instance Group Description")}</span>
                <input
                  type="text"
                  className="instance-group-input"
                  value={newInstanceGroupGroupDescription}
                  onChange={(e) =>
                    setNewInstanceGroupGroupDescription(e.target.value)
                  }
                />
              </div>
            </div>
            <button
              className="primary-btn"
              disabled={
                newInstanceGroupGroupName === "" ||
                newInstanceGroupGroupDescription === "" ||
                addNewInstanceGroupGroupLoading
              }
              onClick={() => {
                setAddNewInstanceGroupGroupLoading(true);
                createInstanceGroup({
                  name: newInstanceGroupGroupName.toLocaleLowerCase(),
                  description: newInstanceGroupGroupDescription,
                })
                  .then((res) => {
                    let data = res.data.instance_group;
                    setAddNewInstanceGroupGroupLoading(false);
                    addNewInstanceGroup(data);
                  })
                  .catch((err) => {
                    console.log(err);
                    setAddNewInstanceGroupGroupLoading(false);
                  });
              }}
            >
              {t("Add New Instance Group")}
            </button>
          </div>
        )}
      </Modal>
    </>
  );
};
